/**
 * useAuth — hook central de autenticacao.
 *
 * Responsabilidades:
 *   - Restaurar sessao no boot do app (getSession + onAuthStateChange)
 *   - Buscar profile do usuario logado e expor pro app
 *   - signIn/signOut com suporte a captcha (web) e demo mode (DEV-ONLY)
 *   - Em PROD, demo mode esta completamente desabilitado:
 *       * signIn rejeita explicitamente o email demo
 *       * todos os caminhos demo sao no-op via guard __DEV__
 *
 * Demo mode:
 *   - So funciona em build de DEV (Metro Bundler com __DEV__ === true)
 *   - Email canonico: DEMO_ACCOUNT_EMAIL (demo-personal@arafit.com.br)
 *   - Em PROD build, a conta sera deletada do banco e o codigo demo
 *     nao executa (defense in depth tripla)
 */
import { useState, useEffect } from 'react';
import { Platform } from 'react-native';
import { Session } from '@supabase/supabase-js';
import * as SecureStore from 'expo-secure-store';
import {
  supabase,
  getDemoClient,
  switchToDemo,
  switchToProd,
  onDemoSessionChange,
  emitDemoSession,
  DEMO_ACCOUNT_EMAIL,
} from '@/lib/supabase';
import { Profile } from '@/lib/types';
import { initAnalytics, clearAnalytics, trackEvent } from '@/lib/analytics';

const DEMO_FLAG_KEY = 'ARAFIT_DEMO_MODE';

const storage = {
  get: (): Promise<string | null> =>
    Platform.OS === 'web'
      ? Promise.resolve(localStorage.getItem(DEMO_FLAG_KEY))
      : SecureStore.getItemAsync(DEMO_FLAG_KEY),
  set: (v: string): Promise<void> =>
    Platform.OS === 'web'
      ? Promise.resolve(localStorage.setItem(DEMO_FLAG_KEY, v))
      : SecureStore.setItemAsync(DEMO_FLAG_KEY, v),
  del: (): Promise<void> =>
    Platform.OS === 'web'
      ? Promise.resolve(localStorage.removeItem(DEMO_FLAG_KEY))
      : SecureStore.deleteItemAsync(DEMO_FLAG_KEY),
};

export function useAuth() {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsubscribeAuth: () => void = () => {};
    let unsubscribeDemoEvent: () => void = () => {};

    async function init() {
      // Restore demo mode so the Proxy points to the right client on app restart.
      // DEV-ONLY: em PROD, qualquer flag de demo no storage e ignorada.
      if (__DEV__) {
        const demoFlag = await storage.get();
        const useLocalForDemo = process.env.EXPO_PUBLIC_DEMO_USE_LOCAL === 'true';
        if (demoFlag === 'true' && !useLocalForDemo) switchToDemo();
      }

      const { data: { session } } = await supabase.auth.getSession();
      setSession(session);
      if (session) fetchProfile(session.user.id);
      else setLoading(false);

      const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
        setSession(session);
        if (session) fetchProfile(session.user.id);
        else { setProfile(null); setLoading(false); }
      });
      unsubscribeAuth = () => subscription.unsubscribe();

      // Listen for demo logins from other useAuth instances (DEV-ONLY, no-op em prod)
      unsubscribeDemoEvent = onDemoSessionChange((session) => {
        setSession(session);
        if (session) fetchProfile(session.user.id);
        else { setProfile(null); setLoading(false); }
      });
    }

    init();
    return () => { unsubscribeAuth(); unsubscribeDemoEvent(); };
  }, []);

  async function fetchProfile(userId: string) {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    setProfile(data);
    setLoading(false);

    if (data) {
      initAnalytics(data.id, data.role);
      trackEvent('app_open', {});
    }
  }

  async function signIn(email: string, password: string, captchaToken?: string) {
    const isDemo = email.trim().toLowerCase() === DEMO_ACCOUNT_EMAIL;

    // Em PROD, demo mode esta desabilitado. Rejeitar explicitamente o email demo
    // com mensagem clara antes de qualquer call ao Supabase.
    if (isDemo && !__DEV__) {
      throw new Error('Conta de demonstracao disponivel apenas em ambiente de desenvolvimento.');
    }

    // FIXME [dev override]: EXPO_PUBLIC_DEMO_USE_LOCAL=true faz a conta demo
    // entrar no Supabase local em vez do remoto. Remover quando nao precisar mais.
    const useLocalForDemo = process.env.EXPO_PUBLIC_DEMO_USE_LOCAL === 'true';

    // captchaToken eh exigido pelo Supabase Auth quando captcha protection esta
    // ON no Dashboard. No AraFit, o widget Turnstile renderiza so no web
    // (Platform.OS === 'web') e passa o token aqui via login.tsx.
    const authOptions = captchaToken ? { captchaToken } : undefined;

    if (isDemo && !useLocalForDemo) {
      // DEV-ONLY path. Em prod, a guarda acima ja rejeitou.
      switchToDemo();
      await storage.set('true');

      const demoClient = getDemoClient();
      const { data, error } = await demoClient.auth.signInWithPassword({ email, password, options: authOptions });
      if (error) {
        switchToProd();
        await storage.del();
        throw error;
      }
      // Notify all useAuth instances about the demo session
      emitDemoSession(data.session);
    } else {
      const { error } = await supabase.auth.signInWithPassword({ email, password, options: authOptions });
      if (error) throw error;
    }
  }

  async function signOut() {
    clearAnalytics();
    await supabase.auth.signOut();
    if (__DEV__) {
      await storage.del();
      switchToProd();
      emitDemoSession(null);
    }
  }

  return { session, profile, loading, signIn, signOut };
}
